import "@/App.css";
import { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from 'sonner';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from './context/AuthContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Navbar } from './components/Navbar';
import { Footer } from './components/Footer';
import { ChatbotWidget } from './components/ChatbotWidget';
import PageTransition from './components/common/PageTransition';

const Home = lazy(() => import('./pages/Home').then(m => ({ default: m.Home })));
const Catalogue = lazy(() => import('./pages/Catalogue').then(m => ({ default: m.Catalogue })));
const Contact = lazy(() => import('./pages/Contact').then(m => ({ default: m.Contact })));
const About = lazy(() => import('./pages/About').then(m => ({ default: m.About })));
const Diaspora = lazy(() => import('./pages/Diaspora').then(m => ({ default: m.Diaspora })));
const PropertyDetail = lazy(() => import('./pages/PropertyDetail').then(m => ({ default: m.PropertyDetail })));
const Comparator = lazy(() => import('./pages/Comparator').then(m => ({ default: m.Comparator })));
const MapPage = lazy(() => import('./pages/MapPage').then(m => ({ default: m.MapPage })));
const CMSDynamicPage = lazy(() => import('./pages/CMSDynamicPage').then(m => ({ default: m.CMSDynamicPage })));
const Investors = lazy(() => import('./pages/Investors').then(m => ({ default: m.Investors })));
const Programs = lazy(() => import('./pages/Programs').then(m => ({ default: m.Programs })));
const Blog = lazy(() => import('./pages/Blog').then(m => ({ default: m.Blog })));
const FAQ = lazy(() => import('./pages/FAQ').then(m => ({ default: m.FAQ })));
const AdminLogin = lazy(() => import('./pages/admin/AdminLogin').then(m => ({ default: m.AdminLogin })));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard').then(m => ({ default: m.AdminDashboard })));
const AdminProperties = lazy(() => import('./pages/admin/AdminProperties').then(m => ({ default: m.AdminProperties })));
const AdminClients = lazy(() => import('./pages/admin/AdminClients').then(m => ({ default: m.AdminClients })));
const PropertyForm = lazy(() => import('./pages/admin/PropertyForm').then(m => ({ default: m.PropertyForm })));
const AdminAnalytics = lazy(() => import('./pages/admin/AdminAnalytics').then(m => ({ default: m.AdminAnalytics })));
const AdminUsers = lazy(() => import('./pages/admin/AdminUsers').then(m => ({ default: m.AdminUsers })));
const AdminSettings = lazy(() => import('./pages/admin/AdminSettings').then(m => ({ default: m.AdminSettings })));
const AdminAgents = lazy(() => import('./pages/admin/AdminAgents').then(m => ({ default: m.AdminAgents })));
const AdminVisits = lazy(() => import('./pages/admin/AdminVisits').then(m => ({ default: m.AdminVisits })));
const AdminLeads = lazy(() => import('./pages/admin/AdminLeads').then(m => ({ default: m.AdminLeads })));
const AdminTransactions = lazy(() => import('./pages/admin/AdminTransactions').then(m => ({ default: m.AdminTransactions })));
const AdminPayments = lazy(() => import('./pages/admin/AdminPayments').then(m => ({ default: m.AdminPayments })));
const AdminContracts = lazy(() => import('./pages/admin/AdminContracts').then(m => ({ default: m.AdminContracts })));
const AdminDocuments = lazy(() => import('./pages/admin/AdminDocuments').then(m => ({ default: m.AdminDocuments })));
const AdminCalendar = lazy(() => import('./pages/admin/AdminCalendar').then(m => ({ default: m.AdminCalendar })));
const AdminNotifications = lazy(() => import('./pages/admin/AdminNotifications').then(m => ({ default: m.AdminNotifications })));
const AdminCMSPages = lazy(() => import('./pages/admin/AdminCMSPages').then(m => ({ default: m.AdminCMSPages })));
const AdminMedia = lazy(() => import('./pages/admin/AdminMedia').then(m => ({ default: m.AdminMedia })));
const AdminBanners = lazy(() => import('./pages/admin/AdminBanners').then(m => ({ default: m.AdminBanners })));

const adminPages = [
  { path: '/admin/dashboard', component: null },
  { path: '/admin/clients', component: AdminClients },
  { path: '/admin/projets', component: AdminLeads },
  { path: '/admin/patrimoine', component: AdminProperties },
  { path: '/admin/transactions', component: AdminTransactions },
  { path: '/admin/documents', component: AdminDocuments },
  { path: '/admin/rapports', component: AdminAnalytics },
  { path: '/admin/utilisateurs', component: AdminUsers },
  { path: '/admin/parametres', component: AdminSettings },
];

const PageLoader = () => (
  <div className="flex flex-col items-center justify-center min-h-screen bg-[#080808] text-white">
    <div className="inline-block animate-spin rounded-full h-12 w-12 border-3 border-[#C9A227] border-t-transparent mb-4" />
    <span className="text-xs font-bold uppercase tracking-widest text-gray-400">Chargement...</span>
  </div>
);

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <div className="App">
          <BrowserRouter>
            <Suspense fallback={<PageLoader />}>
              <PageTransition>
                <Routes>
                  {/* Public Routes */}
                  <Route path="/" element={<><Navbar /><Home /><ChatbotWidget /><Footer /></>} />
                  <Route path="/catalogue" element={<><Navbar /><Catalogue /><ChatbotWidget /><Footer /></>} />
                  <Route path="/contact" element={<><Navbar /><Contact /><ChatbotWidget /><Footer /></>} />
                  <Route path="/about" element={<><Navbar /><About /><ChatbotWidget /><Footer /></>} />
                  <Route path="/diaspora" element={<><Navbar /><Diaspora /><ChatbotWidget /><Footer /></>} />
                  <Route path="/property/:id" element={<><Navbar /><PropertyDetail /><ChatbotWidget /><Footer /></>} />
                  <Route path="/comparateur" element={<><Navbar /><Comparator /><ChatbotWidget /><Footer /></>} />
                  <Route path="/carte" element={<><Navbar /><MapPage /><ChatbotWidget /><Footer /></>} />
                  <Route path="/services" element={<><Navbar /><CMSDynamicPage slug="services" /><ChatbotWidget /><Footer /></>} />
                  <Route path="/faq" element={<><Navbar /><FAQ /><ChatbotWidget /><Footer /></>} />
                  <Route path="/mentions-legales" element={<><Navbar /><CMSDynamicPage slug="mentions-legales" /><ChatbotWidget /><Footer /></>} />
                  <Route path="/programmes" element={<><Navbar /><Programs /><ChatbotWidget /><Footer /></>} />
                  <Route path="/investisseurs" element={<><Navbar /><Investors /><ChatbotWidget /><Footer /></>} />
                  <Route path="/blog" element={<><Navbar /><Blog /><ChatbotWidget /><Footer /></>} />
                  
                  {/* Admin Routes */}
                  <Route path="/admin/login" element={<AdminLogin />} />
                  {adminPages.map(({ path, component: Component }) => (
                    <Route
                      key={path}
                      path={path}
                      element={
                        <ProtectedRoute adminOnly>
                          <AdminDashboard>
                            {Component ? <Component /> : null}
                          </AdminDashboard>
                        </ProtectedRoute>
                      }
                    />
                  ))}
                  <Route 
                    path="/admin/properties/new" 
                    element={
                      <ProtectedRoute adminOnly>
                        <AdminDashboard><PropertyForm /></AdminDashboard>
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/admin/properties/:id/edit" 
                    element={
                      <ProtectedRoute adminOnly>
                        <AdminDashboard><PropertyForm /></AdminDashboard>
                      </ProtectedRoute>
                    } 
                  />
                  {/* Legacy English redirects */}
                  <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
                  <Route path="/admin/contacts" element={<Navigate to="/admin/clients" replace />} />
                  <Route path="/admin/leads" element={<Navigate to="/admin/projets" replace />} />
                  <Route path="/admin/properties" element={<Navigate to="/admin/patrimoine" replace />} />
                  <Route path="/admin/analytics" element={<Navigate to="/admin/rapports" replace />} />
                  <Route path="/admin/users" element={<Navigate to="/admin/utilisateurs" replace />} />
                  <Route path="/admin/settings" element={<Navigate to="/admin/parametres" replace />} />
                </Routes>
              </PageTransition>
            </Suspense>
            <Toaster 
              position="top-right" 
              richColors 
              toastOptions={{
                className: 'rounded-xl border border-gray-200/80 shadow-lg text-xs font-medium',
                duration: 3500
              }}
            />
          </BrowserRouter>
        </div>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
